import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Radio, CheckCircle, Users, Eye, Link as LinkIcon,
  Loader2, Copy, Trophy, Gift, Share2, Zap
} from "lucide-react";
import { cn } from "@/lib/utils";
import { StreamManager } from "@/components/streamer/StreamManager";

export default function StreamerDashboard() {
  const { connected, publicKey, connect } = useWallet();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    displayName: "",
    username: "",
    platform: "twitch",
    profileImage: "",
  });

  const { data: streamer, isLoading } = useQuery({
    queryKey: ["streamer-profile", publicKey],
    queryFn: async () => {
      if (!publicKey) return null;
      const { data, error } = await supabase
        .from("streamers")
        .select("*")
        .eq("user_wallet", publicKey)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!publicKey,
  });

  const { data: stats } = useQuery({
    queryKey: ["streamer-stats", streamer?.id],
    queryFn: async () => {
      if (!streamer?.id) return null;
      const { data: streams } = await supabase
        .from("live_streams")
        .select("viewer_count")
        .eq("streamer_id", streamer.id);
      
      const totalViews = streams?.reduce((sum, s) => sum + (s.viewer_count || 0), 0) || 0;
      return { totalViews, streamCount: streams?.length || 0 };
    },
    enabled: !!streamer?.id,
  });

  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!publicKey) throw new Error("Connect wallet first");
      
      const { error } = await supabase.from("streamers").insert({
        user_wallet: publicKey,
        display_name: form.displayName,
        username: form.username,
        platform: form.platform,
        profile_image: form.profileImage || null,
        is_verified: false,
        is_active: true,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Registration Submitted!", description: "Your streamer application is being reviewed" });
      queryClient.invalidateQueries({ queryKey: ["streamer-profile"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const referralLink = streamer 
    ? `${window.location.origin}?ref=${streamer.username}`
    : null;

  const copyReferralLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      toast({ title: "Copied!", description: "Referral link copied to clipboard" });
    }
  };

  if (!connected) {
    return (
      <>
        <Helmet><title>Streamer Dashboard - TheMoonRun</title></Helmet>
        <PageLayout>
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Radio className="w-16 h-16 text-primary/30 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">Connect Wallet</h2>
              <p className="text-muted-foreground mb-4">Connect your wallet to access streamer features</p>
              <Button variant="hero" onClick={connect}>Connect Wallet</Button>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Helmet><title>Streamer Dashboard - TheMoonRun</title></Helmet>
        <PageLayout>
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </PageLayout>
      </>
    );
  }

  if (!streamer) {
    return (
      <>
        <Helmet><title>Become a Streamer - TheMoonRun</title></Helmet>
        <PageLayout maxWidth="xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Radio className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-display font-bold text-foreground mb-2">Become a Streamer</h1>
            <p className="text-muted-foreground">Register to access exclusive streamer features and earn more</p>
          </div>

          <div className="p-6 rounded-xl bg-card border border-border space-y-6">
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                { icon: Zap, label: "1.25x Vote Power" },
                { icon: Trophy, label: "Verified Badge" },
                { icon: Gift, label: "Giveaway Tools" },
                { icon: Share2, label: "Referral Earnings" },
              ].map((benefit, i) => (
                <div key={i} className="p-3 rounded-lg bg-secondary/50 flex items-center gap-2">
                  <benefit.icon className="w-4 h-4 text-primary" />
                  <span className="text-sm text-foreground">{benefit.label}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Display Name *</Label>
                <Input
                  placeholder="Your name"
                  value={form.displayName}
                  onChange={(e) => setForm(f => ({ ...f, displayName: e.target.value }))}
                />
              </div>
              <div>
                <Label className="mb-2 block">Username *</Label>
                <Input
                  placeholder="@username"
                  value={form.username}
                  onChange={(e) => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Platform *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { id: "twitch", icon: "💜" },
                  { id: "youtube", icon: "🎬" },
                  { id: "tiktok", icon: "📱" },
                  { id: "kick", icon: "🟢" },
                ].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, platform: p.id }))}
                    className={cn(
                      "p-3 rounded-lg border-2 text-sm font-medium capitalize transition-all",
                      form.platform === p.id
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:border-primary/50"
                    )}
                  >
                    {p.icon} {p.id}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Profile Image URL</Label>
              <Input
                placeholder="https://..."
                value={form.profileImage}
                onChange={(e) => setForm(f => ({ ...f, profileImage: e.target.value }))}
              />
            </div>

            <Button
              variant="hero"
              className="w-full"
              disabled={!form.displayName || !form.username || registerMutation.isPending}
              onClick={() => registerMutation.mutate()}
            >
              {registerMutation.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Registering...</>
              ) : (
                <><Radio className="w-4 h-4" /> Register as Streamer</>
              )}
            </Button>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Streamer Dashboard - TheMoonRun</title></Helmet>
      <PageLayout>
        <div className="max-w-5xl mx-auto">
          {/* Profile Header */}
          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border mb-6">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-secondary overflow-hidden flex-shrink-0">
                {streamer.profile_image ? (
                  <img src={streamer.profile_image} alt={streamer.display_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-primary">
                    {streamer.display_name.charAt(0)}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-xl font-bold text-foreground">{streamer.display_name}</h1>
                  {streamer.is_verified && <CheckCircle className="w-5 h-5 text-primary" />}
                  <Badge variant="secondary" className="capitalize">{streamer.platform}</Badge>
                </div>
                <p className="text-muted-foreground">@{streamer.username}</p>
              </div>
              <Badge variant={streamer.is_verified ? "default" : "outline"} className="flex-shrink-0">
                {streamer.is_verified ? "Verified" : "Pending Verification"}
              </Badge>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {[
              { label: "Total Views", value: stats?.totalViews.toLocaleString() || "0", icon: Eye },
              { label: "Streams", value: stats?.streamCount || 0, icon: Radio },
              { label: "Followers", value: streamer.follower_count || 0, icon: Users },
              { label: "Vote Power", value: `${streamer.vote_multiplier || 1}x`, icon: Zap },
            ].map((stat, i) => (
              <div key={i} className="p-3 sm:p-4 rounded-xl bg-card border border-border">
                <stat.icon className="w-5 h-5 text-primary mb-2" />
                <p className="text-xl sm:text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>

          <Tabs defaultValue="streams" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="streams">Streams</TabsTrigger>
              <TabsTrigger value="tools">Tools</TabsTrigger>
              <TabsTrigger value="referrals">Referrals</TabsTrigger>
              <TabsTrigger value="giveaways">Giveaways</TabsTrigger>
            </TabsList>

            <TabsContent value="streams" className="space-y-4">
              <StreamManager />
            </TabsContent>

            <TabsContent value="tools" className="space-y-4">
              <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-primary" />
                  Your Referral Link
                </h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input value={referralLink || ""} readOnly className="font-mono text-sm flex-1" />
                  <Button variant="outline" onClick={copyReferralLink} className="flex-shrink-0">
                    <Copy className="w-4 h-4" /> Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Share this link to earn 0.5% of all trading fees from referred users
                </p>
              </div>

              <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
                <h3 className="font-semibold text-foreground mb-4">Stream Overlay Widget</h3>
                <div className="p-4 rounded-lg bg-secondary/50">
                  <p className="text-sm text-muted-foreground mb-2">
                    Add this URL as a browser source in OBS/Streamlabs:
                  </p>
                  <code className="block p-2 rounded bg-background text-xs font-mono break-all">
                    {`${window.location.origin}/overlay/${streamer.username}`}
                  </code>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="referrals" className="space-y-4">
              <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
                <h3 className="font-semibold text-foreground mb-4">Referral Tracking</h3>
                <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
                  <div className="p-3 sm:p-4 rounded-lg bg-secondary/50 text-center">
                    <p className="text-lg sm:text-2xl font-bold text-foreground">0</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Referrals</p>
                  </div>
                  <div className="p-3 sm:p-4 rounded-lg bg-secondary/50 text-center">
                    <p className="text-lg sm:text-2xl font-bold text-success">0 SOL</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Earned</p>
                  </div>
                  <div className="p-3 sm:p-4 rounded-lg bg-secondary/50 text-center">
                    <p className="text-lg sm:text-2xl font-bold text-foreground">0 SOL</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Pending</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Share your referral link to start earning
                </p>
              </div>
            </TabsContent>

            <TabsContent value="giveaways" className="space-y-4">
              <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-primary" />
                  Create Giveaway
                </h3>
                <p className="text-muted-foreground mb-4">
                  Create giveaways and challenges to reward your community
                </p>
                <Button variant="hero">
                  <Gift className="w-4 h-4" />
                  Create Giveaway
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </PageLayout>
    </>
  );
}
